"use client";

import { z } from "zod";
import { useCommonData } from "../../../../../../../components/layout/CommonDataProvider";
import { SubjectView } from "../../../../../../../searchAndCollection/components/SubjectView";
import { getSearchConfig } from "../../../../../../../searchAndCollection/searchApi/searchConfig";
import { useMultipleUrlStates } from "../../../../../../../utils/hooks/useMultipleUrlStates/useMultipleUrlStates";
import { WorksData } from "../../personer/[agentBibbiIdAndNorafId]/fetchAgentWorksData";
import useSWRInfinite from "swr/infinite";
import { useEffect } from "react";
import { sift } from "radash";
import { fetchWorksForSubject, SubjectPageData } from "./page.common";

export const SubjectPageClientSide = ({ subject, initialWorksData }: SubjectPageData) => {
  const worksData = useFetchMoreWorksAboutSubject(subject.id, initialWorksData);

  return <SubjectView subject={subject} worksData={worksData} />;
};

const subjectUrlStateSchema = z.object({ page: z.number().optional() });

export const useSubjectUrlPagination = () => {
  const [state, setState] = useMultipleUrlStates(subjectUrlStateSchema);
  const paginate = () => setState({ page: state?.page ? state.page + 1 : 2 });
  return { page: state?.page, paginate };
};

const useFetchMoreWorksAboutSubject = (subjectId: string, initialWorksData: WorksData): WorksData => {
  const { site } = useCommonData();
  const searchConfig = getSearchConfig(site);
  const { page } = useSubjectUrlPagination();

  const getKey = (pageIndex: number, previousPageData: WorksData): { subjectId: string; page: number } | null => {
    if (previousPageData && previousPageData.endOfResults) return null;
    return { subjectId, page: pageIndex };
  };

  const { data, isValidating, setSize } = useSWRInfinite(
    getKey,
    ({ page }) => {
      if (page == 0) return undefined;
      return fetchWorksForSubject({ subjectId, page: page + 1, searchConfig });
    },
    { revalidateIfStale: false, revalidateOnFocus: false }
  );

  useEffect(() => {
    setSize(page !== undefined ? page : 1);
  }, [setSize, page]);

  const filteredData = data ? sift(data) : [];
  const newWorkParams = filteredData.flatMap(({ works }) => works.map((work) => work.toParams()));
  const worksParams = sift([...initialWorksData.worksParams, ...newWorkParams]);
  const endOfResults = [initialWorksData, ...filteredData]?.slice(-1)[0]?.endOfResults;

  return {
    worksParams,
    endOfResults: endOfResults !== undefined ? endOfResults : true,
    isValidating,
  };
};
